// BHP-1047: React Query hooks over the Hydra read-model. Caching/staleness is
// handled centrally by the shared QueryClient (shared/query/queryClient.ts).
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { httpClient } from '../http/httpClient';
import {
  type HydraCollection,
  normalizeCollection,
  normalizeItem,
  type RawHydraCollection
} from './hydra';

/** API Platform default page size. */
export const PAGE_SIZE = 30;

/**
 * BHP-1260: the most pages {@link useCollectionAll} will load (1,500 rows). A
 * list that long does not belong on one screen; failing says so instead of
 * firing a request per page.
 */
export const MAX_COLLECTION_PAGES = 50;

/** Query parameters passed to a collection, e.g. API Platform filters. */
export type CollectionParams = Record<string, string | number | undefined>;

/**
 * Undefined values are dropped so an unset filter does not create its own cache
 * entry, and the keys are sorted so argument order never splits the cache.
 */
function paramsKey(params: CollectionParams): string {
  return Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== '')
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}=${String(value)}`)
    .join('&');
}

export const hydraKeys = {
  all: ['hydra'] as const,
  /** Every query of one resource, collections and items alike. */
  resource: (resource: string) => ['hydra', resource] as const,
  collection: (resource: string, page: number, params: CollectionParams = {}) =>
    ['hydra', resource, 'collection', page, paramsKey(params)] as const,
  /** Every page at once; under the same prefix as the paged keys, so one invalidation covers both. */
  collectionAll: (resource: string, params: CollectionParams = {}) =>
    ['hydra', resource, 'collection', 'all', paramsKey(params)] as const,
  item: (resource: string, uuid: string) =>
    ['hydra', resource, 'item', uuid] as const
};

export interface UseCollectionOptions {
  page?: number;
  enabled?: boolean;
  /**
   * BHP-1063: extra query parameters, typically API Platform filters such as
   * `{'dnsZone.uuid': …}`. Part of the cache key, so two filter values never
   * share an entry.
   */
  params?: CollectionParams;
}

/** Paginated collection (`/api/<resource>?page=`), normalized from Hydra. */
export function useCollection<T>(
  resource: string,
  options: UseCollectionOptions = {}
): UseQueryResult<HydraCollection<T>> {
  const page = options.page ?? 1;
  const params = options.params ?? {};
  return useQuery({
    queryKey: hydraKeys.collection(resource, page, params),
    queryFn: () => fetchPage<T>(resource, params, page),
    enabled: options.enabled ?? true
  });
}

async function fetchPage<T>(
  resource: string,
  params: CollectionParams,
  page: number
): Promise<HydraCollection<T>> {
  const raw = await httpClient.get<RawHydraCollection<Record<string, unknown>>>(
    `/${resource}`,
    { params: { ...params, page } }
  );
  return normalizeCollection<T>(raw);
}

/**
 * BHP-1260: every page of a collection, in the order the server sends them.
 *
 * For a page that filters or groups the rows itself (the VPS overview filters
 * on a label and groups per project): with only page 1 such a page silently
 * dropped everything past row 30, including a server just created. Page 1
 * gives the total and the page size; the other pages are asked for at once.
 *
 * Page 1 is fetched with the rest rather than taken from the paged cache (the
 * sidebar's count holds it): a cached page 1 and fresh later pages are two
 * snapshots, and a server created in between shifts a row across the boundary,
 * shown twice or not at all.
 */
export function useCollectionAll<T>(
  resource: string,
  options: Omit<UseCollectionOptions, 'page'> = {}
): UseQueryResult<HydraCollection<T>> {
  const params = options.params ?? {};
  return useQuery({
    queryKey: hydraKeys.collectionAll(resource, params),
    queryFn: async () => {
      const first = await fetchPage<T>(resource, params, 1);
      // The server's page size, not an assumed one: a resource configured
      // differently would otherwise lose rows without a word.
      const pageSize = first.items.length || PAGE_SIZE;
      const pages = Math.ceil(first.total / pageSize);
      if (pages > MAX_COLLECTION_PAGES) {
        throw new Error(
          `/${resource} has ${first.total} rows; not loading more than ${MAX_COLLECTION_PAGES} pages`
        );
      }
      const rest = await Promise.all(
        Array.from({ length: pages - 1 }, (_, i) =>
          fetchPage<T>(resource, params, i + 2)
        )
      );
      return {
        items: [first, ...rest].flatMap((page) => page.items),
        total: first.total
      };
    },
    enabled: options.enabled ?? true
  });
}

export interface UseItemOptions<T> {
  enabled?: boolean;
  /**
   * Poll while the callback returns a delay in ms; return false to stop.
   * Used for resources whose state is settled asynchronously server-side.
   * `fetchCount` is the number of completed fetches, so callers can bound how
   * long they keep polling.
   */
  refetchInterval?: (data: T | undefined, fetchCount: number) => number | false;
  /** Keep polling while the tab is in the background (default: pause). */
  refetchIntervalInBackground?: boolean;
}

/** Single item by uuid (`/api/<resource>/{uuid}`), normalized from Hydra. */
export function useItem<T>(
  resource: string,
  uuid: string,
  options: UseItemOptions<T> | boolean = true
): UseQueryResult<T> {
  // Historic signature: `useItem(resource, uuid, enabled)`.
  const resolved: UseItemOptions<T> =
    typeof options === 'boolean' ? { enabled: options } : options;
  const pollFor = resolved.refetchInterval;

  return useQuery({
    queryKey: hydraKeys.item(resource, uuid),
    queryFn: async () => {
      const raw = await httpClient.get<Record<string, unknown>>(
        `/${resource}/${uuid}`
      );
      return normalizeItem<T>(raw);
    },
    enabled: resolved.enabled ?? true,
    refetchInterval:
      pollFor === undefined
        ? undefined
        : (query) => pollFor(query.state.data, query.state.dataUpdateCount),
    refetchIntervalInBackground: resolved.refetchIntervalInBackground
  });
}
